import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n";
import AppShell from "@/components/layout/AppShell";
import PlatformShell from "@/components/layout/PlatformShell";
import LoginView from "@/views/auth/LoginView";
import SignupView from "@/views/auth/SignupView";
import ExternalAccountantsView from "@/views/tenant/ExternalAccountantsView";
import DevRequestsView from "@/views/tenant/DevRequestsView";
import DevRequestDetail from "@/views/tenant/DevRequestDetail";
import PlatformDevRequestsView from "@/views/platform/PlatformDevRequestsView";
import IndustryTemplatesView from "@/views/platform/IndustryTemplatesView";
import AccountantWorkspaceView, { AcceptInvitationView } from "@/views/tenant/AccountantWorkspace";
import PlatformLoginView from "@/views/auth/PlatformLoginView";
import DashboardView from "@/views/tenant/DashboardView";
import UsersView from "@/views/tenant/UsersView";
import RolesView from "@/views/tenant/RolesView";
import CompaniesView from "@/views/tenant/CompaniesView";
import FilesView from "@/views/tenant/FilesView";
import AuditView from "@/views/tenant/AuditView";
import NotificationsView from "@/views/tenant/NotificationsView";
import SettingsView from "@/views/tenant/SettingsView";
import HelpView from "@/views/tenant/HelpView";
import HelpCenterView, { HelpArticleView, HelpManualView } from "@/views/tenant/HelpCenterView";
import PlatformDocsView from "@/views/platform/PlatformDocsView";
import MigrationView from "@/views/tools/MigrationView";
import BackupView from "@/views/tenant/BackupView";
import PreferencesView from "@/views/tenant/PreferencesView";
import { initTheme } from "@/lib/theme";
import MobileLogin, { PinGate } from "@/views/mobile/MobileLogin";
import PlatformBackupView from "@/views/platform/PlatformBackupView";
import PromoStudio from "@/views/platform/PromoStudio";
import CatalogAdmin from "@/views/platform/CatalogAdmin";
import { AppsIndex, AppPage, IndustriesIndex, IndustryPage } from "@/views/website/Catalog";
import RecoveryWizard from "@/views/platform/RecoveryWizard";
import PlatformDashboardView from "@/views/platform/PlatformDashboardView";
import CustomersView from "@/views/platform/CustomersView";
import LicensesView from "@/views/platform/LicensesView";
import SystemView from "@/views/platform/SystemView";
import PlatformAuditView from "@/views/platform/PlatformAuditView";
import PipelineView from "@/views/crm/PipelineView";
import LeadsView from "@/views/crm/LeadsView";
import ContactsView from "@/views/crm/ContactsView";
import ActivitiesView from "@/views/crm/ActivitiesView";
import InsightsView from "@/views/crm/InsightsView";
import TerritoryMap from "@/views/crm/TerritoryMap";
import HandoverView, { HandoversList } from "@/views/crm/HandoverView";
import SalesTeamsView from "@/views/crm/SalesTeamsView";
import BrandsView from "@/views/sales/BrandsView";
import SalesView from "@/views/sales/SalesView";
import QuickOrder from "@/views/sales/QuickOrder";
import KitchenDisplay from "@/views/pos/KitchenDisplay";
import CrmOverview from "@/views/crm/CrmOverview";
import LearnOrbisPage from "@/views/website/LearnPage";
import AccountingView from "@/views/accounting/AccountingView";
import FinanceView from "@/views/finance/FinanceView";
import ExecView from "@/views/exec/ExecView";
import ManagerView from "@/views/exec/ManagerView";
import VendorUpload from "@/views/website/VendorUpload";
import PurchaseView from "@/views/purchase/PurchaseView";
import TaxView from "@/views/tax/TaxView";
import InventoryView from "@/views/inventory/InventoryView";
import BankView from "@/views/accounting/BankView";
import ReportStudioView from "@/views/reports/ReportStudioView";
import HRView from "@/views/hr/HRView";
import MyHRView from "@/views/hr/MyHRView";
import StudioView from "@/views/studio/StudioView";
import TranslationsView from "@/views/tenant/TranslationsView";
import ProjectsView from "@/views/projects/ProjectsView";
import HelpdeskView from "@/views/helpdesk/HelpdeskView";
import RoutingView, { MyRouteView } from "@/views/routing/RoutingView";
import BillingView from "@/views/platform/BillingView";
import DeveloperView from "@/views/tenant/DeveloperView";
import DocumentsView from "@/views/tenant/DocumentsView";
import PosView from "@/views/pos/PosView";
import SubscriptionsView from "@/views/subscriptions/SubscriptionsView";
import RentalView from "@/views/rental/RentalView";
import ApprovalsView from "@/views/approvals/ApprovalsView";
import KnowledgeView from "@/views/knowledge/KnowledgeView";
import DiscussView from "@/views/discuss/DiscussView";
import PublicArticleView from "@/views/knowledge/PublicArticleView";
import OpsView from "@/views/ops/OpsView";
import PublicSite from "@/views/website/PublicSite";
import WebsiteStudio from "@/views/website/WebsiteStudio";
import HrsView from "@/views/hrs/HrsView";
import BookingPage from "@/views/hrs/BookingPage";
import ShopFloorView from "@/views/ops/ShopFloorView";
import MobileApp from "@/views/mobile/MobileApp";
import MobileSalesManager from "@/views/mobile/MobileSalesManager";
import ShopFloorDashboard from "@/views/ops/ShopFloorDashboard";
import MarketingView from "@/views/marketing/MarketingView";
import { EventPage, SurveyPage, CareersPage } from "@/views/marketing/PublicPages";
import { StatusPublicPage, CheckinPage } from "@/views/marketing/StatusCheckin";
import { PromisePage } from "@/views/marketing/PromisePage";
import { BoardPage } from "@/views/exec/BoardPage";
import MobileStock from "@/views/mobile/MobileStock";
import MobileDelivery from "@/views/mobile/MobileDelivery";
import WebView from "@/views/web/WebView";
import ToolsView from "@/views/tools/ToolsView";
import { BlogPage, ForumPage, LearnPage, SignPage } from "@/views/web/PublicWeb";
import { ShopPage, ProductPage } from "@/views/web/ShopSite";
import OfflineChangesView from "@/views/tenant/OfflineChangesView";
import { BankRedirectView } from "@/views/tenant/PlansPanel";
import { PayLinkView, PaymentResultView } from "@/views/portal/PaymentViews";
import { PortalLoginView, PortalSetPasswordView, PortalHomeView } from "@/views/portal/PortalViews";
import { ForgotPasswordView, ResetPasswordView } from "@/views/auth/PasswordViews";

const Loading = () => <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground font-mono" data-testid="app-loading">loading…</div>;

function TenantGuard() {
  const { status } = useAuth();
  if (status === "loading") return <Loading />;
  if (status === "platform") return <Navigate to="/platform-admin" replace />;
  if (status !== "tenant") return <Navigate to="/login" replace />;
  return <AppShell><Outlet /></AppShell>;
}

initTheme("binance");

function MobileGuard() {
  const { status } = useAuth();
  if (status === "loading") return <Loading />;
  if (status !== "tenant") return <Navigate to="/m/login" replace />;
  return <PinGate><MobileApp /></PinGate>;
}

function BareTenantGuard() {
  const { status } = useAuth();
  if (status === "loading") return <Loading />;
  if (status !== "tenant") return <Navigate to="/login" replace />;
  return <Outlet />;
}

function PlatformGuard({ bare = false }) {
  const { status } = useAuth();
  if (status === "loading") return <Loading />;
  if (status === "tenant") return <Navigate to="/app/dashboard" replace />;
  if (status !== "platform") return <Navigate to="/platform-admin/login" replace />;
  if (bare) return <Outlet />;
  return <PlatformShell><Outlet /></PlatformShell>;
}

function Home() {
  const { status } = useAuth();
  if (status === "loading") return <Loading />;
  if (status === "platform" || status === "tenant") return <Navigate to={status === "platform" ? "/platform-admin" : "/app/dashboard"} replace />;
  return <PublicSite />;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/apps" element={<AppsIndex />} />
            <Route path="/learn" element={<LearnOrbisPage />} />
            <Route path="/apps/:slug" element={<AppPage />} />
            <Route path="/industries" element={<IndustriesIndex />} />
            <Route path="/industries/:slug" element={<IndustryPage />} />
            <Route path="/login" element={<LoginView />} />
            <Route path="/vendor-upload/:token" element={<VendorUpload />} />
            <Route path="/m/login" element={<MobileLogin />} />
            <Route path="/m/*" element={<MobileGuard />} />
            <Route path="/:slug" element={<PublicSite />} />
            <Route path="/w/:site" element={<PublicSite tenant />} />
            <Route path="/w/:site/:slug" element={<PublicSite tenant />} />
            <Route path="/book/:site/:slug" element={<BookingPage />} />
            <Route path="/events/:site/:slug" element={<EventPage />} />
            <Route path="/s/:token" element={<SurveyPage />} />
            <Route path="/status/:site" element={<StatusPublicPage />} />
            <Route path="/checkin/:token" element={<CheckinPage />} />
            <Route path="/promise/:token" element={<PromisePage />} />
            <Route path="/board/:token" element={<BoardPage />} />
            <Route path="/shop/:site" element={<ShopPage />} />
            <Route path="/shop/:site/p/:pslug" element={<ProductPage />} />
            <Route path="/blog/:site" element={<BlogPage />} />
            <Route path="/blog/:site/:slug" element={<BlogPage />} />
            <Route path="/forum/:site" element={<ForumPage />} />
            <Route path="/forum/:site/:thread" element={<ForumPage />} />
            <Route path="/learn/:site" element={<LearnPage />} />
            <Route path="/learn/:site/:slug" element={<LearnPage />} />
            <Route path="/sign/:token" element={<SignPage />} />
            <Route path="/careers" element={<PublicSite />} />
            <Route path="/w/:site/careers" element={<PublicSite tenant />} />
            <Route path="/app/website-studio" element={<BareTenantGuard />}><Route index element={<WebsiteStudio standalone />} /></Route>
            <Route path="/shopfloor" element={<BareTenantGuard />}><Route index element={<ShopFloorView />} /><Route path="dashboard" element={<ShopFloorDashboard />} /></Route>
            <Route path="/m/stock" element={<BareTenantGuard />}><Route index element={<MobileStock />} /></Route>
            <Route path="/m/deliveries" element={<BareTenantGuard />}><Route index element={<MobileDelivery />} /><Route path=":id" element={<MobileDelivery />} /></Route>
            <Route path="/kb/:token" element={<PublicArticleView />} />
            <Route path="/signup" element={<SignupView />} />
            <Route path="/accountant/accept" element={<AcceptInvitationView />} />
            <Route path="/platform-admin/login" element={<PlatformLoginView />} />
            <Route path="/forgot-password" element={<ForgotPasswordView />} />
            <Route path="/reset-password" element={<ResetPasswordView />} />
            <Route path="/app" element={<TenantGuard />}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardView />} />
              <Route path="users" element={<UsersView />} />
              <Route path="settings/accountants" element={<ExternalAccountantsView />} />
              <Route path="settings/development" element={<DevRequestsView />} />
              <Route path="settings/development/:id" element={<DevRequestDetail />} />
              <Route path="accountant" element={<AccountantWorkspaceView />} />
              <Route path="roles" element={<RolesView />} />
              <Route path="companies" element={<CompaniesView />} />
              <Route path="files" element={<FilesView />} />
              <Route path="audit" element={<AuditView />} />
              <Route path="notifications" element={<NotificationsView />} />
              <Route path="settings" element={<SettingsView />} />
              <Route path="help" element={<HelpCenterView />} />
              <Route path="help/manual" element={<HelpView />} />
              <Route path="help/a/:slug" element={<HelpArticleView />} />
              <Route path="help/m/:role" element={<HelpManualView />} />
              <Route path="migration" element={<MigrationView />} />
              <Route path="backup" element={<BackupView />} />
              <Route path="preferences" element={<PreferencesView />} />
              <Route path="mobile-sales" element={<MobileSalesManager />} />
              <Route path="crm" element={<CrmOverview />} />
              <Route path="crm/pipeline" element={<PipelineView />} />
              <Route path="crm/leads" element={<LeadsView />} />
              <Route path="crm/contacts" element={<ContactsView />} />
              <Route path="crm/activities" element={<ActivitiesView />} />
              <Route path="crm/insights" element={<InsightsView />} />
              <Route path="crm/territory" element={<TerritoryMap />} />
              <Route path="crm/handovers" element={<HandoversList />} />
              <Route path="crm/handovers/:id" element={<HandoverView />} />
              <Route path="crm/teams" element={<SalesTeamsView />} />
              <Route path="sales/brands" element={<BrandsView />} />
              <Route path="sales" element={<SalesView />} />
              <Route path="sales/quick-order" element={<QuickOrder />} />
              <Route path="accounting" element={<AccountingView />} />
              <Route path="finance" element={<FinanceView />} />
              <Route path="exec/:dept/team" element={<ManagerView />} />
              <Route path="exec/:dept" element={<ExecView />} />
              <Route path="purchase" element={<PurchaseView />} />
              <Route path="tax" element={<TaxView />} />
              <Route path="inventory" element={<InventoryView />} />
              <Route path="bank" element={<BankView />} />
              <Route path="reports" element={<ReportStudioView />} />
              <Route path="hr" element={<HRView />} />
              <Route path="my-hr" element={<MyHRView />} />
              <Route path="studio" element={<StudioView />} />
              <Route path="translations" element={<TranslationsView />} />
              <Route path="projects" element={<ProjectsView />} />
              <Route path="helpdesk" element={<HelpdeskView />} />
              <Route path="routing" element={<RoutingView />} />
              <Route path="my-route" element={<MyRouteView />} />
              <Route path="billing/bank/:orderId" element={<BankRedirectView />} />
              <Route path="developer" element={<DeveloperView />} />
              <Route path="documents" element={<DocumentsView />} />
              <Route path="pos" element={<PosView />} />
              <Route path="pos/kitchen" element={<KitchenDisplay />} />
              <Route path="subscriptions" element={<SubscriptionsView />} />
              <Route path="rental" element={<RentalView />} />
              <Route path="approvals" element={<ApprovalsView />} />
              <Route path="knowledge" element={<KnowledgeView />} />
              <Route path="discuss" element={<DiscussView />} />
              <Route path="website" element={<WebsiteStudio />} />
              {["manufacturing", "quality", "maintenance", "plm", "fleet"].map((m) => <Route key={m} path={m} element={<OpsView module={m} />} />)}
              {["recruitment", "appraisals", "referrals", "planning", "fieldservice", "appointments"].map((m) => <Route key={m} path={m} element={<HrsView module={m} />} />)}
              {["marketing", "events", "surveys"].map((m) => <Route key={m} path={m} element={<MarketingView module={m} />} />)}
              {["ecommerce", "blog", "forum", "elearning", "livechat"].map((m) => <Route key={m} path={m} element={<WebView module={m} />} />)}
              {["spreadsheets", "sign", "esg", "voip", "iot"].map((m) => <Route key={m} path={m} element={<ToolsView module={m} />} />)}
              <Route path="offline-changes" element={<OfflineChangesView />} />
            </Route>
            <Route path="/portal/login" element={<PortalLoginView />} />
            <Route path="/portal/set-password" element={<PortalSetPasswordView />} />
            <Route path="/portal" element={<PortalHomeView />} />
            <Route path="/pay/:invoiceId" element={<PayLinkView />} />
            <Route path="/payment/success" element={<PaymentResultView ok />} />
            <Route path="/payment/cancel" element={<PaymentResultView ok={false} />} />
            <Route path="/platform-admin/website" element={<PlatformGuard bare />}><Route index element={<WebsiteStudio standalone />} /></Route>
            <Route path="/platform-admin" element={<PlatformGuard />}>
              <Route index element={<PlatformDashboardView />} />
              <Route path="customers" element={<CustomersView />} />
              <Route path="licenses" element={<LicensesView />} />
              <Route path="billing" element={<BillingView />} />
              <Route path="development" element={<PlatformDevRequestsView />} />
              <Route path="industry-templates" element={<IndustryTemplatesView />} />
              <Route path="system" element={<SystemView />} />
              <Route path="backup" element={<PlatformBackupView />} />
              <Route path="docs" element={<PlatformDocsView />} />
              <Route path="recovery" element={<RecoveryWizard />} />
              <Route path="promo" element={<PromoStudio />} />
              <Route path="catalog" element={<CatalogAdmin />} />
              <Route path="audit" element={<PlatformAuditView />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </I18nProvider>
  );
}
